// frontend/frontend/src/pages/RespresentanteLegal.js
import React, { useEffect, useState } from "react";
import "./Proprietarios.css";
import { jwtDecode } from "jwt-decode";
import { copiarTexto } from "../utils/clipboard";
import ThemeProvider from "../theme/TemaMuiProvider";
import { Box, Button, TablePagination } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import muiTheme from "../theme/muiTheme";
import { FaEdit } from "react-icons/fa";

// =======================
// MÁSCARA CPF
// =======================
function maskCPF(value) {
  return String(value ?? "")
    .replace(/\D/g, "")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2")
    .substring(0, 14);
}

// =======================
// MÁSCARA CEP
// =======================
function maskCEP(value) {
  return String(value ?? "")
    .replace(/\D/g, "")
    .replace(/(\d{5})(\d)/, "$1-$2")
    .substring(0, 9);
}

// =======================
// CONSULTA VIA CEP
// =======================
async function buscarCEP(cep) {
  try {
    const semMascara = String(cep ?? "").replace(/\D/g, "");
    if (semMascara.length !== 8) return null;

    const response = await fetch(`https://viacep.com.br/ws/${semMascara}/json/`);

    if (!response.ok) return null;

    const data = await response.json();

    if (data.erro) return null;

    return {
      logradouro: data.logradouro || "",
      bairro: data.bairro || "",
      cidade: data.localidade || "",
      estado: data.uf || "",
    };
  } catch (err) {
    console.error("Erro ao consultar CEP:", err);
    return null;
  }
}

function RespresentanteLegal() {
  const [responsaveisLegais, setResponsaveisLegais] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [novoResponsavelLegal, setNovoResponsavelLegal] = useState({
    documento: "",
    nome: "",
    nacionalidade: "",
    estadoCivil: "",
    cep: "",
    logradouro: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    estado: "",
    observacao: "",
  });

  const [editandoId, setEditandoId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [responsaveisPorPagina, setResponsaveisPorPagina] = useState(5);
  const [modoVisualizacao, setModoVisualizacao] = useState(false);
  const [idVisualizando, setIdVisualizando] = useState(null);
  const [mostrarPendencias, setMostrarPendencias] = useState(false);

  const [gridMinimizado, setGridMinimizado] = useState(() => {
    const saved = localStorage.getItem("responsavelLegalGridMinimizado");
    return saved ? saved === "1" : false;
  });

  useEffect(() => {
    localStorage.setItem("responsavelLegalGridMinimizado", gridMinimizado ? "1" : "0");
  }, [gridMinimizado]);

  // =======================
  // SISTEMA DE PERMISSÕES
  // =======================
  const token = localStorage.getItem("token");
  let tipoUsuario = "";

  if (token) {
    try {
      const decoded = jwtDecode(token);

      tipoUsuario =
        decoded.tipo
          ?.toString()
          .trim()
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "") || "";
    } catch (err) {
      console.error("Erro ao decodificar token:", err);
    }
  }

  const podeVisualizar = ["visitante", "usuario padrao", "gestor", "administrador"].includes(tipoUsuario);

  const podeCadastrar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);

  const podeEditar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);

  const podeExcluir = ["gestor", "administrador"].includes(tipoUsuario);

  // =======================
  // CARREGAR LISTA
  // =======================
  const carregarResponsaveisLegais = async () => {
    const tokenAtual = localStorage.getItem("token");

    try {
      setLoading(true);

      const response = await fetch("/api/responsaveis-legais", {
        headers: {
          Authorization: `Bearer ${tokenAtual}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Erro HTTP ${response.status}`);
      }

      const data = await response.json();

      const lista = Array.isArray(data) ? data : Array.isArray(data?.responsaveisLegais) ? data.responsaveisLegais : [];

      setResponsaveisLegais(lista);
    } catch (err) {
      console.error("Erro ao buscar responsáveis legais:", err);
      setResponsaveisLegais([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarResponsaveisLegais();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =======================
  // RESET FORM
  // =======================
  const resetForm = () => {
    setNovoResponsavelLegal({
      documento: "",
      nome: "",
      nacionalidade: "",
      estadoCivil: "",
      cep: "",
      logradouro: "",
      numero: "",
      complemento: "",
      bairro: "",
      cidade: "",
      estado: "",
      observacao: "",
    });

    setEditandoId(null);
    setIdVisualizando(null);
    setModoVisualizacao(false);
    setMostrarPendencias(false);
  };

  // =======================
  // FILTRO
  // =======================
  const termos = filtro
    .split(",")
    .map((termo) => termo.trim().toLowerCase())
    .filter(Boolean);

  const filtrados = responsaveisLegais.filter((responsavel) => {
    const texto = [
      responsavel.documento,
      responsavel.cpf,
      responsavel.nome,
      responsavel.nacionalidade,
      responsavel.estadoCivil,
      responsavel.cep,
      responsavel.logradouro,
      responsavel.rua,
      responsavel.numero,
      responsavel.complemento,
      responsavel.bairro,
      responsavel.cidade,
      responsavel.estado,
      responsavel.observacao,
    ]
      .join(" ")
      .toLowerCase();

    return termos.every((termo) => texto.includes(termo));
  });

  // =======================
  // PAGINAÇÃO
  // =======================
  const totalRegistros = filtrados.length;

  const totalPaginas = Math.max(1, Math.ceil(totalRegistros / responsaveisPorPagina));

  const paginaSegura = Math.min(paginaAtual, totalPaginas);

  const indexInicial = (paginaSegura - 1) * responsaveisPorPagina;

  const indexFinal = Math.min(indexInicial + responsaveisPorPagina, totalRegistros);

  const registrosPaginados = filtrados.slice(indexInicial, indexFinal);

  // =======================
  // ESTADO FORM
  // =======================
  const formTravado = modoVisualizacao && !editandoId;

  const temRegistroAberto = Boolean(idVisualizando || editandoId);

  const idParaExcluirNoForm = editandoId || idVisualizando;

  // =======================
  // CAMPOS OPCIONAIS
  // =======================
  const camposOpcionais = ["nacionalidade", "estadoCivil", "cep", "logradouro", "numero", "complemento", "bairro", "cidade", "estado", "observacao"];

  const estaVazio = (valor) => !String(valor ?? "").trim();

  const classeCampoOpcional = (campo) => {
    if (!mostrarPendencias) return "";

    return camposOpcionais.includes(campo) && estaVazio(novoResponsavelLegal[campo]) ? "campo-pendente" : "";
  };

  // =======================
  // ATUALIZAR CAMPO
  // =======================
  const atualizarCampo = (campo, valor) => {
    setNovoResponsavelLegal((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  };

  // =======================
  // CEP
  // =======================
  const handleCEPChange = async (event) => {
    if (formTravado) return;

    const cepFormatado = maskCEP(event.target.value);

    setNovoResponsavelLegal((prev) => ({
      ...prev,
      cep: cepFormatado,
    }));

    const cepLimpo = cepFormatado.replace(/\D/g, "");

    if (cepLimpo.length !== 8) return;

    const endereco = await buscarCEP(cepFormatado);

    if (!endereco) return;

    setNovoResponsavelLegal((prev) => ({
      ...prev,
      cep: cepFormatado,
      logradouro: String(endereco.logradouro || "").toUpperCase(),
      bairro: String(endereco.bairro || "").toUpperCase(),
      cidade: String(endereco.cidade || "").toUpperCase(),
      estado: String(endereco.estado || "").toUpperCase(),
    }));
  };

  // =======================
  // CADASTRAR / ATUALIZAR
  // =======================
  const handleCadastrar = async (event) => {
    event.preventDefault();

    setMostrarPendencias(true);

    if (!editandoId && !podeCadastrar) {
      alert("Você não tem permissão para cadastrar.");
      return;
    }

    if (editandoId && !podeEditar) {
      alert("Você não tem permissão para editar.");
      return;
    }

    const tokenAtual = localStorage.getItem("token");

    const cpfLimpo = String(novoResponsavelLegal.documento ?? "").replace(/\D/g, "");

    if (cpfLimpo.length !== 11) {
      alert("CPF inválido. Deve possuir 11 números.");
      return;
    }

    if (!novoResponsavelLegal.nome.trim()) {
      alert("Preencha o nome do responsável legal.");
      return;
    }

    const payload = {
      documento: cpfLimpo,
      cpf: cpfLimpo,
      nome: novoResponsavelLegal.nome.trim().toUpperCase(),
      nacionalidade: novoResponsavelLegal.nacionalidade.trim().toUpperCase(),
      estadoCivil: novoResponsavelLegal.estadoCivil,
      cep: novoResponsavelLegal.cep,
      logradouro: novoResponsavelLegal.logradouro.trim().toUpperCase(),
      numero: novoResponsavelLegal.numero.trim().toUpperCase(),
      complemento: novoResponsavelLegal.complemento.trim().toUpperCase(),
      bairro: novoResponsavelLegal.bairro.trim().toUpperCase(),
      cidade: novoResponsavelLegal.cidade.trim().toUpperCase(),
      estado: novoResponsavelLegal.estado.trim().toUpperCase(),
      observacao: novoResponsavelLegal.observacao.trim().toUpperCase(),
    };

    const id = editandoId;

    const url = id ? `/api/responsaveis-legais/${id}` : "/api/responsaveis-legais";

    const method = id ? "PUT" : "POST";

    try {
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tokenAtual}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.erro || data?.mensagem || "Erro ao salvar responsável legal.");
      }

      if (id) {
        setResponsaveisLegais((prev) =>
          prev.map((responsavel) =>
            String(responsavel._id || responsavel.id) === String(id)
              ? {
                  ...responsavel,
                  ...payload,
                  id: responsavel.id || id,
                  _id: responsavel._id,
                }
              : responsavel,
          ),
        );

        resetForm();
        return;
      }

      const idNovo = data?.id || data?._id || data?.responsavelLegal?.id || data?.responsavelLegal?._id;

      const novoRegistro = {
        id: idNovo,
        ...payload,
      };

      setResponsaveisLegais((prev) => [novoRegistro, ...prev]);

      setPaginaAtual(1);
      resetForm();
    } catch (err) {
      console.error("Erro ao salvar responsável legal:", err);

      alert(err?.message || "Erro ao salvar responsável legal.");
    }
  };

  // =======================
  // VISUALIZAR
  // =======================
  const handleVisualizar = (responsavel) => {
    if (!podeVisualizar) {
      alert("Você não tem permissão para visualizar.");
      return;
    }

    const id = responsavel._id || responsavel.id;

    setNovoResponsavelLegal({
      documento: maskCPF(responsavel.documento || responsavel.cpf || ""),
      nome: responsavel.nome || "",
      nacionalidade: responsavel.nacionalidade || "",
      estadoCivil: responsavel.estadoCivil || "",
      cep: maskCEP(responsavel.cep || ""),
      logradouro: responsavel.logradouro || responsavel.rua || "",
      numero: responsavel.numero || "",
      complemento: responsavel.complemento || "",
      bairro: responsavel.bairro || "",
      cidade: responsavel.cidade || "",
      estado: responsavel.estado || "",
      observacao: responsavel.observacao || "",
    });

    setIdVisualizando(id);
    setEditandoId(null);
    setModoVisualizacao(true);
    setMostrarPendencias(true);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =======================
  // EXCLUIR
  // =======================
  const handleExcluir = async (responsavelOuId) => {
    if (!podeExcluir) {
      alert("Você não tem permissão para excluir.");
      return;
    }

    const tokenAtual = localStorage.getItem("token");

    const isObj = typeof responsavelOuId === "object" && responsavelOuId !== null;

    const id = isObj ? responsavelOuId._id || responsavelOuId.id : responsavelOuId;

    const nome = isObj ? responsavelOuId.nome : novoResponsavelLegal.nome;

    if (!id) {
      alert("Selecione um responsável legal para excluir.");
      return;
    }

    const confirmou = window.confirm(`Excluir responsável legal "${nome}"?`);

    if (!confirmou) return;

    try {
      const response = await fetch(`/api/responsaveis-legais/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${tokenAtual}`,
        },
      });

      if (!response.ok) {
        throw new Error("Erro ao excluir responsável legal.");
      }

      setResponsaveisLegais((prev) => prev.filter((responsavel) => String(responsavel._id || responsavel.id) !== String(id)));

      if (String(idVisualizando) === String(id) || String(editandoId) === String(id)) {
        resetForm();
      }
    } catch (err) {
      console.error("Erro ao excluir responsável legal:", err);

      alert(err?.message || "Erro ao excluir responsável legal.");
    }
  };

  // =======================
  // CSV
  // =======================
  const handleCSV = () => {
    const BOM = "\uFEFF";

    const header = [
      "Documento",
      "Nome",
      "Nacionalidade",
      "Estado Civil",
      "CEP",
      "Logradouro",
      "Número",
      "Complemento",
      "Bairro",
      "Cidade",
      "Estado",
      "Observação",
    ];

    const csvEscape = (value) => {
      const texto = String(value ?? "");

      if (/[;"\n\r]/.test(texto)) {
        return `"${texto.replace(/"/g, '""')}"`;
      }

      return texto;
    };

    const excelText = (value) => `="${String(value ?? "")}"`;

    const linhas = filtrados.map((responsavel) =>
      [
        excelText(maskCPF(responsavel.documento || responsavel.cpf || "")),
        responsavel.nome || "",
        responsavel.nacionalidade || "",
        responsavel.estadoCivil || "",
        excelText(maskCEP(responsavel.cep || "")),
        responsavel.logradouro || responsavel.rua || "",
        excelText(responsavel.numero || ""),
        responsavel.complemento || "",
        responsavel.bairro || "",
        responsavel.cidade || "",
        responsavel.estado || "",
        responsavel.observacao || "",
      ].map(csvEscape),
    );

    const csv = [header.map(csvEscape), ...linhas].map((linha) => linha.join(";")).join("\n");

    const blob = new Blob([BOM + csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;

    link.setAttribute("download", "responsaveis-legais.csv");

    document.body.appendChild(link);

    link.click();

    link.remove();

    URL.revokeObjectURL(url);
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <div className="local-container">
        <style>{`
          .campo-formulario-padrao,
          .campo-formulario-padrao:focus,
          .campo-formulario-padrao:active,
          .campo-formulario-padrao:read-only,
          .campo-formulario-padrao:disabled {
            background: transparent !important;
            background-color: transparent !important;
            color: var(--ig-t-ffffff) !important;
            box-shadow: none !important;
            -webkit-text-fill-color: var(--ig-t-ffffff) !important;
            opacity: 1 !important;
          }

          .campo-formulario-padrao::placeholder {
            color: var(--ig-t-255-255-255-a072) !important;
            -webkit-text-fill-color: var(--ig-t-255-255-255-a072) !important;
          }

          .responsavel-observacao {
            min-height: 100px;
            resize: vertical;
          }
        `}</style>

        <h2>Representante Legal</h2>

        {/* FILTRO */}
        <Box
          className="bloco-linha"
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) auto",
            alignItems: "center",
            gap: 2,
            mb: 2,
          }}
        >
          <Box
            sx={{
              height: 52,
              width: "100%",
              border: "1px solid var(--ig-b-555555)",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              backgroundColor: "transparent",
              boxSizing: "border-box",
              px: 2,
            }}
          >
            <input
              type="text"
              placeholder="Buscar Representante Legal"
              value={filtro}
              onChange={(event) => setFiltro(event.target.value)}
              style={{
                flex: 1,
                height: "100%",
                border: "none",
                outline: "none",
                background: "transparent",
                color: "var(--ig-t-ffffff)",
                fontSize: "1rem",
              }}
            />

            <SearchIcon
              sx={{
                color: "var(--ig-t-9ca3af)",
                fontSize: 20,
                cursor: "pointer",
                flexShrink: 0,
              }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Button
              size="small"
              variant="outlined"
              type="button"
              onClick={() => setFiltro("")}
              sx={{
                height: 52,
                color: "#2196f3",
                borderColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  borderColor: "#42a5f5",
                  backgroundColor: "rgba(33, 150, 243, 0.08)",
                },
              }}
            >
              Limpar Filtro
            </Button>

            <Button
              size="small"
              variant="contained"
              type="button"
              onClick={async () => {
                const texto = filtrados.map((responsavel) => responsavel.nome).join(", ");

                if (!texto.trim()) {
                  alert("Nada para copiar (lista vazia).");
                  return;
                }

                const ok = await copiarTexto(texto);

                alert(ok ? "Copiado!" : "Não foi possível copiar.");
              }}
              sx={{
                height: 52,
                backgroundColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#1976d2",
                },
              }}
            >
              <i
                className="fas fa-copy"
                style={{
                  marginRight: 6,
                }}
              />
              Copiar
            </Button>

            <Button
              size="small"
              variant="contained"
              type="button"
              onClick={handleCSV}
              sx={{
                height: 52,
                backgroundColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#1976d2",
                },
              }}
            >
              CSV
            </Button>
          </Box>
        </Box>

        {/* MINIMIZAR / EXPANDIR */}
        <div className="grid-toggle-area right">
          {!gridMinimizado ? (
            <button
              type="button"
              className="btn-grid-toggle small danger"
              onClick={() => setGridMinimizado(true)}
              title="Minimizar o Grid"
              aria-label="Minimizar o Grid"
            >
              ↘ Minimizar
            </button>
          ) : (
            <button
              type="button"
              className="btn-grid-toggle small outline"
              onClick={() => setGridMinimizado(false)}
              title="Expandir o Grid"
              aria-label="Expandir o Grid"
            >
              ↗ Expandir
            </button>
          )}
        </div>

        {/* LISTA */}
        {!gridMinimizado && (
          <div className="bloco-lista">
            {loading ? (
              <p>Carregando Responsáveis Legais...</p>
            ) : filtrados.length === 0 ? (
              <p className="erro">Nenhum responsável legal encontrado.</p>
            ) : (
              <>
                <table>
                  <thead>
                    <tr>
                      <th>Nome do Representante Legal</th>

                      <th>CPF</th>

                      <th>Cidade</th>

                      <th>Estado</th>

                      <th>Ações</th>
                    </tr>
                  </thead>

                  <tbody>
                    {registrosPaginados.map((responsavel) => (
                      <tr key={responsavel._id || responsavel.id}>
                        <td>{responsavel.nome}</td>

                        <td>{maskCPF(responsavel.documento || responsavel.cpf || "")}</td>

                        <td>{responsavel.cidade}</td>

                        <td>{responsavel.estado}</td>

                        <td className="col-acoes">
                          {podeVisualizar && (
                            <button className="btn-editar" type="button" onClick={() => handleVisualizar(responsavel)}>
                              <i className="fas fa-eye" /> Visualizar
                            </button>
                          )}

                          {podeExcluir && (
                            <button className="btn-excluir" type="button" onClick={() => handleExcluir(responsavel)}>
                              <i className="fas fa-trash" /> Excluir
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <TablePagination
                  component="div"
                  count={filtrados.length}
                  page={paginaSegura - 1}
                  onPageChange={(_, novaPagina) => setPaginaAtual(novaPagina + 1)}
                  rowsPerPage={responsaveisPorPagina}
                  onRowsPerPageChange={(event) => {
                    setResponsaveisPorPagina(parseInt(event.target.value, 10));

                    setPaginaAtual(1);
                  }}
                  rowsPerPageOptions={[5, 10, 25]}
                  labelRowsPerPage="Responsáveis por página:"
                  labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                  sx={{
                    fontSize: "0.8rem",
                    minHeight: 42,
                    color: "var(--ig-t-ffffff)",
                    borderTop: "1px solid var(--ig-b-444444)",
                    "& .MuiTablePagination-toolbar": {
                      minHeight: 42,
                    },
                    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                      fontSize: "0.8rem",
                    },
                    "& .MuiTablePagination-selectIcon": {
                      color: "var(--ig-t-ffffff)",
                    },
                    "& .MuiTablePagination-actions button": {
                      color: "var(--ig-t-ffffff)",
                      width: "auto",
                    },
                    "& .MuiTablePagination-actions": {
                      display: "flex",
                      alignItems: "center",
                    },
                  }}
                />
              </>
            )}
          </div>
        )}

        {/* FORMULÁRIO */}
        {(podeCadastrar || podeVisualizar) && (
          <div className="bloco-formulario">
            <form onSubmit={handleCadastrar} className="local-form">
              {/* DOCUMENTO + NOME */}
              <div
                className="linha-dupla"
                style={{
                  gap: 12,
                  alignItems: "flex-end",
                }}
              >
                <div
                  style={{
                    flex: "1 1 0",
                    minWidth: 220,
                  }}
                >
                  <label>Documento*</label>

                  <input
                    value={novoResponsavelLegal.documento}
                    onChange={(event) => {
                      if (formTravado) return;

                      atualizarCampo("documento", maskCPF(event.target.value));
                    }}
                    placeholder="Digite o CPF"
                    required
                    disabled={formTravado}
                    className="campo-formulario-padrao"
                  />
                </div>

                <div
                  style={{
                    flex: "2 1 0",
                    minWidth: 280,
                  }}
                >
                  <label>Nome*</label>

                  <input
                    value={novoResponsavelLegal.nome}
                    onChange={(event) => atualizarCampo("nome", event.target.value.toUpperCase())}
                    placeholder="Digite o nome do responsável legal"
                    required
                    disabled={formTravado}
                    className="campo-formulario-padrao"
                  />
                </div>
              </div>

              {/* NACIONALIDADE + ESTADO CIVIL */}
              <div className="linha-dupla">
                <div>
                  <label>Nacionalidade</label>

                  <input
                    value={novoResponsavelLegal.nacionalidade}
                    onChange={(event) => atualizarCampo("nacionalidade", event.target.value.toUpperCase())}
                    disabled={formTravado}
                    className={`campo-formulario-padrao ${classeCampoOpcional("nacionalidade")}`.trim()}
                  />
                </div>

                <div>
                  <label>Estado Civil</label>

                  <select
                    value={novoResponsavelLegal.estadoCivil}
                    onChange={(event) => atualizarCampo("estadoCivil", event.target.value)}
                    disabled={formTravado}
                    className={classeCampoOpcional("estadoCivil")}
                    style={{
                      backgroundColor: "var(--ig-f-1e1e1e)",
                      color: "var(--ig-t-ffffff)",
                    }}
                  >
                    <option value="">Selecione o Estado Civil</option>

                    <option>Solteiro(a)</option>

                    <option>Casado(a)</option>

                    <option>Divorciado(a)</option>

                    <option>Viúvo(a)</option>

                    <option>União Estável</option>
                  </select>
                </div>
              </div>

              {/* CEP + LOGRADOURO + Nº */}
              <div className="linha-tripla">
                <div>
                  <label>CEP</label>

                  <input
                    value={novoResponsavelLegal.cep}
                    onChange={handleCEPChange}
                    placeholder="Digite o CEP"
                    disabled={formTravado}
                    className={`campo-formulario-padrao ${classeCampoOpcional("cep")}`.trim()}
                  />
                </div>

                <div>
                  <label>Logradouro</label>

                  <input
                    value={novoResponsavelLegal.logradouro}
                    onChange={(event) => atualizarCampo("logradouro", event.target.value.toUpperCase())}
                    disabled={formTravado}
                    className={`campo-formulario-padrao ${classeCampoOpcional("logradouro")}`.trim()}
                  />
                </div>

                <div>
                  <label>Nº</label>

                  <input
                    value={novoResponsavelLegal.numero}
                    onChange={(event) => atualizarCampo("numero", event.target.value.toUpperCase())}
                    disabled={formTravado}
                    className={`campo-formulario-padrao ${classeCampoOpcional("numero")}`.trim()}
                  />
                </div>
              </div>

              {/* COMPLEMENTO + BAIRRO */}
              <div className="linha-dupla">
                <div>
                  <label>Complemento</label>

                  <input
                    value={novoResponsavelLegal.complemento}
                    onChange={(event) => atualizarCampo("complemento", event.target.value.toUpperCase())}
                    disabled={formTravado}
                    className={`campo-formulario-padrao ${classeCampoOpcional("complemento")}`.trim()}
                  />
                </div>

                <div>
                  <label>Bairro</label>

                  <input
                    value={novoResponsavelLegal.bairro}
                    onChange={(event) => atualizarCampo("bairro", event.target.value.toUpperCase())}
                    disabled={formTravado}
                    className={`campo-formulario-padrao ${classeCampoOpcional("bairro")}`.trim()}
                  />
                </div>
              </div>

              {/* CIDADE + ESTADO */}
              <div className="linha-dupla">
                <div>
                  <label>Cidade</label>

                  <input
                    value={novoResponsavelLegal.cidade}
                    onChange={(event) => atualizarCampo("cidade", event.target.value.toUpperCase())}
                    disabled={formTravado}
                    className={`campo-formulario-padrao ${classeCampoOpcional("cidade")}`.trim()}
                  />
                </div>

                <div>
                  <label>Estado</label>

                  <input
                    value={novoResponsavelLegal.estado}
                    onChange={(event) => atualizarCampo("estado", event.target.value.toUpperCase())}
                    disabled={formTravado}
                    maxLength={2}
                    className={`campo-formulario-padrao ${classeCampoOpcional("estado")}`.trim()}
                  />
                </div>
              </div>

              {/* OBSERVAÇÃO */}
              <label>Observação</label>

              <textarea
                value={novoResponsavelLegal.observacao}
                onChange={(event) => atualizarCampo("observacao", event.target.value.toUpperCase())}
                placeholder="Digite uma observação"
                disabled={formTravado}
                className={`campo-formulario-padrao responsavel-observacao ${classeCampoOpcional("observacao")}`.trim()}
              />

              {/* AÇÕES */}
              <div className="acoes-form-proprietarios">
                {modoVisualizacao && !editandoId && podeEditar && (
                  <button
                    type="button"
                    className="btn-cadastrar"
                    onClick={() => {
                      setEditandoId(idVisualizando);

                      setModoVisualizacao(false);
                    }}
                  >
                    <FaEdit /> Editar
                  </button>
                )}

                {(!modoVisualizacao || editandoId) && (
                  <button type="submit" className="btn-cadastrar">
                    <i className="fas fa-save" />

                    <span>{editandoId ? "Atualizar" : "Cadastrar"}</span>
                  </button>
                )}

                {(modoVisualizacao || editandoId) && (
                  <button type="button" className="btn-cancelar" onClick={resetForm}>
                    <i className="fas fa-times" /> Cancelar
                  </button>
                )}

                {temRegistroAberto && podeExcluir && (
                  <button type="button" className="btn-excluir" onClick={() => handleExcluir(idParaExcluirNoForm)}>
                    <i className="fas fa-trash" /> Excluir
                  </button>
                )}
              </div>
            </form>
          </div>
        )}
      </div>
    </ThemeProvider>
  );
}

export default RespresentanteLegal;
